import React from "react";
import { useNavigate } from "react-router-dom";
import { JoinClassroomPanel } from "../dashboard/JoinClassroomPanel";
import { HomeIndexSection } from "./HomeIndexSection";
import { useStudentAuth } from "../../hooks/useStudentAuth";
import { useStudentClassrooms } from "../../hooks/useStudentClassrooms";
import { useTeacherId } from "../../hooks/useTeacherId";
import { useTeacherClassrooms } from "../../hooks/useTeacherClassrooms";
import { HOME_THEME as T } from "../../lib/homeTheme";

interface HomeClassesIndexProps {
  isTeacher: boolean;
}

export const HomeClassesIndex: React.FC<HomeClassesIndexProps> = ({ isTeacher }) => {
  const navigate = useNavigate();
  const { profile, user } = useStudentAuth();
  const { teacherId, teacherName } = useTeacherId();

  const studentId = profile?.uid ?? user?.uid ?? "";
  const studentName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : user?.displayName?.trim() || "Estudiante";

  const { memberships, loading: studentLoading } = useStudentClassrooms(
    !isTeacher ? studentId : undefined,
    studentName
  );
  const { classrooms, membersByClassroom, loading: teacherLoading } = useTeacherClassrooms(
    isTeacher ? teacherId : undefined,
    teacherName
  );

  const loading = isTeacher ? teacherLoading : studentLoading;

  const items = isTeacher
    ? classrooms.slice(0, 5).map((cls) => {
        const count = membersByClassroom(cls.id).length;
        return {
          label: cls.title,
          meta: `${count} alumno${count !== 1 ? "s" : ""} · ${cls.id}`,
          onClick: () => navigate(`/clases/${cls.id}`),
        };
      })
    : memberships.slice(0, 5).map((m) => ({
        label: m.classroomTitle,
        meta: `${m.teacherName || "Profesor"} · ${m.classroomCode}`,
        onClick: () => navigate(`/clases/${m.classroomCode}`),
      }));

  return (
    <div>
      <HomeIndexSection title="Mis clases" items={loading ? [] : items} compact>
        {!isTeacher && studentId ? (
          <div className="mt-2 max-w-md">
            <JoinClassroomPanel
              studentId={studentId}
              studentName={studentName}
              variant="minimal"
            />
          </div>
        ) : null}
      </HomeIndexSection>

      {loading && (
        <p className="-mt-4 pb-2 text-left text-xs tracking-widest uppercase animate-pulse" style={{ color: T.dim }}>
          Cargando clases…
        </p>
      )}

      {!loading && items.length === 0 && (
        <p className="-mt-4 pb-2 text-left text-sm" style={{ color: T.dim }}>
          {isTeacher
            ? "Crea tu primera clase desde el índice completo."
            : "Únete con el código de 8 caracteres que te dio tu profesor."}
        </p>
      )}

      <button
        type="button"
        onClick={() => navigate("/clases")}
        className="text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-[#c4b5fd]"
        style={{ color: T.muted }}
      >
        Ver todas las clases →
      </button>
    </div>
  );
};
